import React, {JSX, ReactElement, RefObject, useCallback, useImperativeHandle, useRef, useState} from "react";

type Api = {
    show: (dialog: DialogFunction) => void;
    open: boolean;
};

export type DialogFunction = (open: boolean, close: (e?: any, reason?: string) => void) => ReactElement;

export type DialogNodeProps = {
    api: RefObject<Api | undefined>;
};

export const DialogNode = ({ api }: DialogNodeProps) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(false);
    const [dialogFnc, setDialogFnc] = useState<DialogFunction | null>(null);

    // Pending timers from the close animation. They tear the dialog down in two steps (active=false,
    // then dialogFnc=null). If a new dialog is show()n while these are still pending — e.g. the same
    // dialog is reopened immediately after closing — the stale timers would clobber the freshly opened
    // dialog (active flipped back to false / dialogFnc reset to null), leaving it permanently blank.
    // Track them so show() can cancel any in-flight teardown before opening the next dialog.
    const closeTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const clearCloseTimers = useCallback(() => {
        closeTimersRef.current.forEach(clearTimeout);
        closeTimersRef.current = [];
    }, []);

    const closeHandler = useCallback((e?: any, reason?: string, followUp: DialogFunction | null = null) => {
        if (reason === "backdropClick") {
            return;
        }
        setOpen(false);
        const outer = setTimeout(() => {
            setActive(false);
            const inner = setTimeout(() => {
                setDialogFnc(followUp);
            }, 190);
            closeTimersRef.current.push(inner);
        }, 20);
        closeTimersRef.current.push(outer);
    }, []);

    const show = useCallback((dialog: DialogFunction) => {
        clearCloseTimers();
        setDialogFnc(() => dialog);
        setActive(true);
        setOpen(true);
    }, [clearCloseTimers]);

    useImperativeHandle(
        api,
        () => ({
            show,
            open
        }),
        [show, open]
    );

    return active && dialogFnc?.(open, closeHandler);
};

export const useDialogController = (): [
    Anker: () => JSX.Element,
    show: (arg: (open: boolean, close: () => void) => ReactElement) => void
] => {
    const api = useRef<Api>(undefined);

    const show = useCallback((fnc: DialogFunction) => {
        api.current?.show(fnc);
    }, []);

    const Node = useCallback(() => <DialogNode api={api} />, []);

    return [Node, show];
};

export const onClose = (close: () => void, handleDialogClosed: () => void) => () => {
    close();
    setTimeout(handleDialogClosed, 320);
};
